import React, { useState } from 'react';
import { Box, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import dynamic from 'next/dynamic';
import LazyYouTube from '../LazyYoutube';
import { facilitiesIconMap } from '../../utils/facilitiesIconMap';
import { provisionIconMap } from '../../utils/provisionIconMap';
import { useDetail } from '../../pages_handler/listPage/client-side/DetailContext';
import { FacilityGrid, StyledTitleSection } from '../projectDetailSection/ProjectDetailContent';
import { DescriptionBox } from '../projectDetailSection/DescriptionBox';
import { PropertyInfo } from './PropertyInfo';
import { specialIconMap } from '../../utils/specialIconMap';
import { extractYouTubeVideoId } from "../../utils/utils";

const AgentCard = dynamic(() => import('../detailPageLayout/AgentCard'), {
  ssr: false,
});

const LeafletMap = dynamic(() => import('../LeafletMap'), {
  ssr: false,
});

const PropertyDetailContent = ({ data, t, locale, nearbyList }) => {
  const lang = locale.toUpperCase();
  const [selectedTab, setSelectedTab] = useState(0);
  const { sectionRefs } = useDetail();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <Box display="flex" alignItems="flex-start" gap={3}>
      {/* Content Area */}
      <Box flex={1} minWidth={0}>
        <Box ref={sectionRefs.current.info} my={4}>
          <PropertyInfo
            propertyInfo={{
              ...data,
            }}
            t={t}
            lang={lang}
          />
        </Box>

        {(data?.Youtube || data?.youtube_url) && (
          <Box my={4}>
            <LazyYouTube videoId={data?.Youtube || extractYouTubeVideoId(data?.youtube_url)} />
          </Box>
        )}

        <Box ref={sectionRefs.current.description} my={4}>
          <StyledTitleSection variant="h2" gutterBottom>
            {t('description')}
          </StyledTitleSection>
          <DescriptionBox t={t} data={data} lang={lang} />
        </Box>

        <Box ref={sectionRefs.current.status} my={4}>
          <StyledTitleSection variant="h2" gutterBottom>
            {t('Status')}
          </StyledTitleSection>
          <FacilityGrid items={data?.Specail || []} lang={lang} iconMap={specialIconMap} type="specail_Name" />
        </Box>

        <Box ref={sectionRefs.current.facilities} my={4}>
          <StyledTitleSection variant="h2" gutterBottom>
            {t('project-facilities')}
          </StyledTitleSection>
          <FacilityGrid
            items={data?.House_Facilities || data?.Apartment_Service || []}
            lang={lang}
            iconMap={facilitiesIconMap}
          />
        </Box>

        <Box ref={sectionRefs.current.provision} my={4}>
          <StyledTitleSection variant="h2" gutterBottom>
            {t('internal-equipment')}
          </StyledTitleSection>
          <FacilityGrid items={data?.Provision || []} lang={lang} iconMap={provisionIconMap} />
        </Box>

        <Box ref={sectionRefs.current.map} my={4}>
          <StyledTitleSection variant="h2" gutterBottom>
            {t('map')}
          </StyledTitleSection>
          <LeafletMap lat={data?.project_Latitude} lng={data?.project_Longitude} />
        </Box>

        <Box ref={sectionRefs.current.location} my={4}>
          <StyledTitleSection variant="h2" gutterBottom>
            {t('nearbyLocation.location')}
          </StyledTitleSection>

          {nearbyList?.length > 0 && (
            <>
              <Box
                sx={{
                  backgroundColor: 'grey.300',
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  overflow: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => setSelectedTab(newValue)}
                  variant="scrollable"
                  scrollButtons={'auto'}
                  allowScrollButtonsMobile
                  aria-label={t('nearbyLocation.location')}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  sx={{
                    '& .MuiTabs-scrollButtons': {
                      width: 'auto',
                    },
                    '& .MuiTabs-scrollButtons.Mui-disabled': {
                      display: 'none',
                    },
                  }}
                >
                  {nearbyList.map((tab, index) => (
                    <Tab
                      key={tab.key}
                      label={tab.label}
                      value={index}
                      component="h3"
                      wrapped
                      sx={{
                        fontSize: '1rem',
                        minWidth: 130,
                        textTransform: 'none',
                        bgcolor: (theme) => (selectedTab === index ? theme.palette.primary.main : 'transparent'),
                        transition: '0.3s',
                      }}
                      style={{
                        color: selectedTab === index ? '#fff' : '#000',
                      }}
                    />
                  ))}
                </Tabs>
              </Box>
              {nearbyList[selectedTab]?.children}
            </>
          )}
        </Box>
      </Box>

      {/* AgentCard Sidebar */}
      {isDesktop && (
        <Box
          sx={{
            width: 326,
            position: 'sticky',
            top: 24,
            alignSelf: 'flex-start',
            flexShrink: 0,
            my: 2,
          }}
        >
          <AgentCard
            id={data?.property_ID}
            type={'property'}
            locale={locale}
            popOverState={() => {}}
            setPopOverState={() => {}}
            inquiryState={() => {}}
            setInquiryState={() => {}}
            appointmentState={() => {}}
            setAppointmentState={() => {}}
            details={data}
            isProject={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default PropertyDetailContent;
